import React, { useState, useEffect, useCallback, useMemo } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  updateDoc,
  getDocs,
  deleteDoc,
  orderBy,
} from "firebase/firestore";
import { setLogLevel } from "firebase/firestore";

// --- Listas de Raças (Integrado) ---
const dogBreeds = [
  "Sem Raça Definida (SRD)",
  "Affenpinscher",
  "Airedale Terrier",
  "Akita",
  "American Staffordshire Terrier",
  "Australian Shepherd",
  "Basenji",
  "Basset Hound",
  "Beagle",
  "Bernese Mountain Dog",
  "Bichon Frise",
  "Border Collie",
  "Boston Terrier",
  "Boxer",
  "Bulldog",
  "Cavalier King Charles Spaniel",
  "Chihuahua",
  "Cocker Spaniel",
  "Collie",
  "Dachshund",
  "Dalmatian",
  "Doberman Pinscher",
  "English Springer Spaniel",
  "French Bulldog",
  "German Shepherd",
  "Golden Retriever",
  "Great Dane",
  "Havanese",
  "Labrador Retriever",
  "Lhasa Apso",
  "Maltese",
  "Mastiff",
  "Miniature Schnauzer",
  "Newfoundland",
  "Pomeranian",
  "Poodle",
  "Pug",
  "Rottweiler",
  "Shetland Sheepdog",
  "Shih Tzu",
  "Siberian Husky",
  "West Highland White Terrier",
  "Yorkshire Terrier",
];
const catBreeds = [
  "Sem Raça Definida (SRD)",
  "Abyssinian",
  "American Shorthair",
  "Bengal",
  "Birman",
  "British Shorthair",
  "Burmese",
  "Exotic Shorthair",
  "Himalayan",
  "Maine Coon",
  "Oriental",
  "Persian",
  "Ragdoll",
  "Russian Blue",
  "Scottish Fold",
  "Siamese",
  "Siberian",
  "Sphynx",
  "Tonkinese",
];

// --- Lógica e Protocolos Vacinais (Integrado) ---
const vaccineProtocols = {
  dog: {
    mandatory: [
      { name: "V8 ou V10 (1ª dose)", dueWeeks: 6 },
      { name: "V8 ou V10 (2ª dose)", dueWeeks: 9 },
      { name: "V8 ou V10 (3ª dose)", dueWeeks: 12 },
      { name: "V8 ou V10 (Reforço Anual)", dueWeeks: 52, recurring: true },
      {
        name: "Antirrábica",
        dueWeeks: 16,
        recurring: true,
        recurringWeeks: 52,
      },
    ],
    optional: [
      { name: "Gripe (dose inicial)", dueWeeks: 8 },
      { name: "Gripe (reforço)", dueWeeks: 12 },
      { name: "Gripe (Reforço Anual)", dueWeeks: 52, recurring: true },
      { name: "Giárdia (dose inicial)", dueWeeks: 12 },
      { name: "Giárdia (reforço)", dueWeeks: 15 },
      { name: "Giárdia (Reforço Anual)", dueWeeks: 52, recurring: true },
      {
        name: "Leishmaniose",
        dueWeeks: 16,
        recurring: true,
        recurringWeeks: 52,
      },
    ],
  },
  cat: {
    mandatory: [
      { name: "V4 ou V5 (1ª dose)", dueWeeks: 8 },
      { name: "V4 ou V5 (2ª dose)", dueWeeks: 12 },
      { name: "V4 ou V5 (Reforço Anual)", dueWeeks: 52, recurring: true },
      {
        name: "Antirrábica",
        dueWeeks: 16,
        recurring: true,
        recurringWeeks: 52,
      },
    ],
    optional: [
      {
        name: "FeLV (para gatos não testados ou com acesso à rua)",
        dueWeeks: 8,
        recurring: true,
        recurringWeeks: 52,
      },
    ],
  },
};

function addWeeks(date, weeks) {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + weeks * 7);
  return newDate;
}

function generateVaccineCalendar(pet, history) {
  if (!pet.birthDate) {
    return { mandatory: [], optional: [] };
  }

  const protocol = vaccineProtocols[pet.species];
  if (!protocol) {
    return { mandatory: [], optional: [] };
  }

  const birthDate = new Date(pet.birthDate + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const processVaccineList = (list) => {
    return list.map((vaccineRule) => {
      const historyEntry = history.find((h) =>
        h.vaccineName
          .toLowerCase()
          .includes(vaccineRule.name.split(" ")[0].toLowerCase())
      );

      if (historyEntry) {
        return {
          name: vaccineRule.name,
          status: "Aplicada",
          dueDate: new Date(historyEntry.date),
        };
      }

      const originalDueDate = addWeeks(birthDate, vaccineRule.dueWeeks);
      const rescheduledDateStr = pet.rescheduledDates?.[vaccineRule.name];

      if (rescheduledDateStr) {
        const rescheduledDate = new Date(rescheduledDateStr + "T00:00:00");
        if (rescheduledDate >= today) {
          return {
            name: vaccineRule.name,
            status: "Reagendado",
            dueDate: rescheduledDate,
            originalDueDate,
          };
        }
      }

      if (originalDueDate < today) {
        return {
          name: vaccineRule.name,
          status: "Atrasada",
          dueDate: originalDueDate,
        };
      }

      return {
        name: vaccineRule.name,
        status: "Próxima Dose",
        dueDate: originalDueDate,
      };
    });
  };

  const mandatorySchedule = processVaccineList(protocol.mandatory);
  const optionalSchedule = processVaccineList(protocol.optional).map((v) =>
    v.status === "Atrasada" ? { ...v, status: "Recomendada" } : v
  );

  return {
    mandatory: mandatorySchedule,
    optional: optionalSchedule,
  };
}

// --- Ícones (SVG) ---
const PawIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6 inline-block"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"
    />
  </svg>
);
const HeartIcon = ({ filled }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`h-6 w-6 transition-all duration-200 ${
      filled ? "text-red-500 fill-current" : "text-gray-400"
    }`}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
    ></path>
  </svg>
);
const BookmarkIcon = ({ filled }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`h-6 w-6 transition-all duration-200 ${
      filled ? "text-blue-500 fill-current" : "text-gray-400"
    }`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
    />
  </svg>
);
const SparklesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M5 2a1 1 0 00-1 1v1h1a1 1 0 010 2H4v1a1 1 0 001 1h2a1 1 0 011 1v2a1 1 0 001 1h1a1 1 0 001-1v-1a1 1 0 012 0v1a1 1 0 001 1h1a1 1 0 001-1v-2a1 1 0 01-1-1h-2a1 1 0 00-1-1H9a1 1 0 01-1-1V5a1 1 0 00-1-1H5z"
      clipRule="evenodd"
    />
    <path d="M9 11a1 1 0 00-1 1v1h1a1 1 0 010 2H8v1a1 1 0 001 1h2a1 1 0 011 1v2a1 1 0 001 1h1a1 1 0 001-1v-1a1 1 0 012 0v1a1 1 0 001 1h1a1 1 0 001-1v-2a1 1 0 01-1-1h-2a1 1 0 00-1-1h-1a1 1 0 01-1-1v-1a1 1 0 00-1-1H9z" />
  </svg>
);
const HappyFaceIcon = ({ filled }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`h-6 w-6 cursor-pointer transition-colors duration-200 ${
      filled
        ? "text-green-500 stroke-green-500"
        : "text-gray-400 hover:text-green-500"
    }`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);
const SadFaceIcon = ({ filled }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`h-6 w-6 cursor-pointer transition-colors duration-200 ${
      filled
        ? "text-red-500 stroke-red-500"
        : "text-gray-400 hover:text-red-500"
    }`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);
const SyringeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5 mr-2 inline-block"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M10 3a1 1 0 011 1v5.586l2.293-2.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 9.586V4a1 1 0 011-1z"
      clipRule="evenodd"
    />
    <path
      fillRule="evenodd"
      d="M3 13a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
      clipRule="evenodd"
    />
  </svg>
);
const CheckCircle = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5 text-green-500"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);
const ExclamationCircle = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5 text-red-500"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
      clipRule="evenodd"
    />
  </svg>
);
const ClockIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5 text-yellow-500"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
      clipRule="evenodd"
    />
  </svg>
);
const PlusCircleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5 text-gray-500"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
      clipRule="evenodd"
    />
  </svg>
);
const BellIcon = ({ hasNotification }) => (
  <div className="relative">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
    {hasNotification && (
      <span className="absolute top-0 right-0 block h-2 w-2 transform translate-x-1/2 -translate-y-1/2 rounded-full bg-red-500 ring-2 ring-white"></span>
    )}
  </div>
);

// --- Configuração do Firebase ---
const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
};

// --- Inicialização do Firebase ---
let app;
try {
  app = initializeApp(firebaseConfig);
} catch (e) {
  console.error("Firebase initialization error", e);
}
const auth = getAuth(app);
const db = getFirestore(app);
setLogLevel("error");

// --- Componentes ---
// A ordem dos componentes foi ajustada para evitar ReferenceError.

const AuthComponent = ({ setAuthError, authError }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [state, setStateValue] = useState("");
  const [isLogin, setIsLogin] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAuthError("");

    if (isLogin) {
      try {
        await signInWithEmailAndPassword(auth, email, password);
      } catch (error) {
        setAuthError("Email ou senha incorretos.");
      }
    } else {
      if (!name || !phone || !city || !state) {
        setAuthError("Por favor, preencha todos os campos do cadastro.");
        return;
      }
      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        const user = userCredential.user;
        await sendEmailVerification(user);
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + 3);
        const userDocRef = doc(db, "users", user.uid);
        await setDoc(userDocRef, {
          name,
          phone,
          city,
          state,
          email: user.email,
          createdAt: new Date().toISOString(),
          subscriptionTier: "free",
          subscriptionEndDate: expirationDate.toISOString(),
          isAdmin: false,
        });
      } catch (error) {
        if (error.code === "auth/email-already-in-use") {
          setAuthError("Este email já está cadastrado. Tente fazer login.");
        } else if (error.code === "auth/weak-password") {
          setAuthError("A senha precisa ter pelo menos 6 caracteres.");
        } else {
          setAuthError("Ocorreu um erro ao criar a conta.");
        }
      }
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <PawIcon />
          <h2 className="mt-4 text-3xl font-extrabold text-gray-900">
            {isLogin ? "Bem-vindo de volta!" : "Crie sua conta"}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {isLogin
              ? "Acesse para criar receitas incríveis."
              : "Preencha os dados e ganhe 3 dias de acesso gratuito!"}
          </p>
        </div>
        {authError && (
          <p className="text-sm text-center text-red-600 bg-red-100 p-2 rounded-md">
            {authError}
          </p>
        )}
        <form className="space-y-4" onSubmit={handleSubmit}>
          {!isLogin && (
            <>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome Completo"
                required
                className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Telefone"
                required
                className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <div className="flex space-x-4">
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Cidade"
                  required
                  className="w-1/2 px-4 py-2 text-gray-700 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setStateValue(e.target.value)}
                  placeholder="Estado"
                  required
                  className="w-1/2 px-4 py-2 text-gray-700 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </>
          )}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Senha (mínimo 6 caracteres)"
            required
            className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            type="submit"
            className="w-full py-3 font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-300"
          >
            {isLogin ? "Entrar" : "Cadastrar e Iniciar Teste"}
          </button>
        </form>
        <p className="text-sm text-center text-gray-600">
          {isLogin ? "Não tem uma conta?" : "Já tem uma conta?"}
          <button
            onClick={() => {
              setIsLogin(!isLogin);
              setAuthError("");
            }}
            className="ml-1 font-medium text-indigo-600 hover:text-indigo-500"
          >
            {isLogin ? "Cadastre-se" : "Faça login"}
          </button>
        </p>
      </div>
    </div>
  );
};

const EmailVerificationMessage = ({ user }) => {
  const [message, setMessage] = useState("");

  const handleResend = async () => {
    setMessage("");
    try {
      await sendEmailVerification(user);
      setMessage(
        "Email de verificação reenviado com sucesso! Verifique sua caixa de entrada."
      );
    } catch (error) {
      setMessage(
        "Ocorreu um erro ao reenviar o email. Tente novamente mais tarde."
      );
      console.error(error);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md text-center">
        <h2 className="text-2xl font-bold text-gray-800">
          Verifique seu E-mail
        </h2>
        <p className="text-gray-600">
          Enviamos um link de verificação para{" "}
          <span className="font-semibold">{user.email}</span>. Por favor, clique
          no link para ativar sua conta e acessar o Pet Chef AI.
        </p>
        <p className="text-sm text-gray-500">
          Se não encontrar, verifique sua caixa de spam.
        </p>
        {message && (
          <p className="text-sm text-green-600 bg-green-100 p-2 rounded-md">
            {message}
          </p>
        )}
        <div className="flex flex-col space-y-4 pt-4">
          <button
            onClick={handleResend}
            className="w-full py-2 font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-300"
          >
            Reenviar E-mail de Verificação
          </button>
          <button
            onClick={() => signOut(auth)}
            className="w-full py-2 font-semibold text-indigo-600 bg-transparent border border-indigo-600 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-300"
          >
            Fazer Login com Outra Conta
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const usersRef = collection(db, "users");
    const unsubscribe = onSnapshot(
      usersRef,
      (snapshot) => {
        const usersData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersData);
        setError("");
        setLoading(false);
      },
      (err) => {
        console.error("Erro ao buscar usuários:", err);
        if (err.code === "permission-denied") {
          setError(
            "Permissão negada. A sua conta precisa ser de administrador e as Regras de Segurança do Firestore devem permitir a listagem de usuários por administradores."
          );
        } else {
          setError("Ocorreu um erro ao buscar os dados dos usuários.");
        }
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const handleSubscriptionChange = async (userId, field, value) => {
    const userDocRef = doc(db, "users", userId);
    try {
      await updateDoc(userDocRef, { [field]: value });
    } catch (error) {
      console.error("Erro ao atualizar usuário:", error);
      setError(
        "Não foi possível atualizar o usuário. Verifique suas permissões de administrador."
      );
    }
  };

  if (loading) return <p>Carregando usuários do painel de administração...</p>;

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold mb-6">Painel Administrativo</h2>
        <div
          className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4"
          role="alert"
        >
          <p className="font-bold">Erro de Acesso</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <h2 className="text-3xl font-bold mb-6">Painel Administrativo</h2>
      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Email
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Tipo de Assinatura
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Fim da Assinatura
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Pet / Peso(kg)
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((u) => (
              <tr key={u.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                  {u.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={u.subscriptionTier}
                    onChange={(e) =>
                      handleSubscriptionChange(
                        u.id,
                        "subscriptionTier",
                        e.target.value
                      )
                    }
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  >
                    <option value="free">Gratuita</option>
                    <option value="monthly">Mensal</option>
                    <option value="inactive">Inativa</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="date"
                    value={
                      u.subscriptionEndDate
                        ? u.subscriptionEndDate.split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      handleSubscriptionChange(
                        u.id,
                        "subscriptionEndDate",
                        new Date(e.target.value).toISOString()
                      )
                    }
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {u.name || "N/A"} / {u.weight || "N/A"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AccountPage = ({ user, userData }) => {
  const cancelSubscription = async () => {
    if (
      window.confirm(
        "Tem certeza que deseja cancelar sua assinatura? Você perderá o acesso aos recursos premium no final do período de cobrança."
      )
    ) {
      const userDocRef = doc(db, "users", user.uid);
      try {
        await updateDoc(userDocRef, {
          subscriptionTier: "free", // ou 'cancelled'
        });
        alert("Sua assinatura foi cancelada.");
      } catch (error) {
        console.error("Erro ao cancelar assinatura:", error);
        alert("Não foi possível cancelar a assinatura. Tente novamente.");
      }
    }
  };
  return (
    <div className="max-w-7xl mx-auto">
      <h2 className="text-3xl font-bold mb-6">Minha Conta</h2>
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h3 className="text-xl font-semibold mb-4">Detalhes da Assinatura</h3>
        <p>
          <strong>Plano Atual:</strong>{" "}
          <span className="capitalize">{userData.subscriptionTier}</span>
        </p>
        <p>
          <strong>Status:</strong>{" "}
          {new Date(userData.subscriptionEndDate) > new Date() ? (
            <span className="text-green-600 font-semibold">Ativa</span>
          ) : (
            <span className="text-red-600 font-semibold">Expirada</span>
          )}
        </p>
        <p>
          <strong>Válida até:</strong>{" "}
          {new Date(userData.subscriptionEndDate).toLocaleDateString()}
        </p>
        {userData.subscriptionTier !== "free" &&
          new Date(userData.subscriptionEndDate) > new Date() && (
            <button
              onClick={cancelSubscription}
              className="mt-4 px-4 py-2 bg-red-600 text-white font-semibold rounded-lg shadow-md hover:bg-red-700"
            >
              Cancelar Assinatura
            </button>
          )}
      </div>

      {userData?.isAdmin && <AdminPanel />}
    </div>
  );
};

const PaymentModal = ({ plan, onClose, onConfirm }) => {
  const [paymentMethod, setPaymentMethod] = useState("card");
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
        <h2 className="text-2xl font-bold mb-2">Finalizar Pagamento</h2>
        <p className="mb-6 text-gray-600">
          Você está assinando o{" "}
          <span className="font-semibold">{plan.name}</span> por{" "}
          <span className="font-semibold">{plan.price}</span>.
        </p>
        <div className="flex border-b mb-4">
          <button
            onClick={() => setPaymentMethod("card")}
            className={`flex-1 py-2 text-sm font-medium ${
              paymentMethod === "card"
                ? "border-b-2 border-indigo-500 text-indigo-600"
                : "text-gray-500"
            }`}
          >
            Cartão de Crédito
          </button>
          <button
            onClick={() => setPaymentMethod("pix")}
            className={`flex-1 py-2 text-sm font-medium ${
              paymentMethod === "pix"
                ? "border-b-2 border-indigo-500 text-indigo-600"
                : "text-gray-500"
            }`}
          >
            PIX
          </button>
        </div>

        {paymentMethod === "card" && (
          <div className="space-y-4">
            <input
              placeholder="Número do Cartão"
              className="w-full p-2 border rounded-md"
            />
            <input
              placeholder="Nome no Cartão"
              className="w-full p-2 border rounded-md"
            />
            <div className="flex space-x-4">
              <input
                placeholder="MM/AA"
                className="w-1/2 p-2 border rounded-md"
              />
              <input
                placeholder="CVC"
                className="w-1/2 p-2 border rounded-md"
              />
            </div>
          </div>
        )}

        {paymentMethod === "pix" && (
          <div className="text-center">
            <p className="mb-2">Escaneie o código QR para pagar com PIX:</p>
            <img
              src="https://placehold.co/200x200/e2e8f0/333?text=QR+Code+PIX"
              alt="QR Code PIX"
              className="mx-auto border rounded-md"
            />
            <button className="mt-4 w-full p-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
              Copiar Código
            </button>
          </div>
        )}

        <div className="mt-8 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="px-6 py-2 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700"
          >
            Pagar
          </button>
        </div>
      </div>
    </div>
  );
};

const SubscriptionPage = ({ user }) => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSelectPlan = (plan, days) => {
    setSelectedPlan({ ...plan, days });
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = async () => {
    if (!selectedPlan) return;
    setProcessing(true);
    const newEndDate = new Date();
    newEndDate.setDate(newEndDate.getDate() + selectedPlan.days);

    try {
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, {
        subscriptionTier: selectedPlan.tier,
        subscriptionEndDate: newEndDate.toISOString(),
      });
      alert(`Plano ${selectedPlan.name} assinado com sucesso!`);
    } catch (error) {
      console.error("Erro ao atualizar assinatura:", error);
      alert("Ocorreu um erro ao processar sua assinatura.");
    } finally {
      setProcessing(false);
      setShowPaymentModal(false);
      setSelectedPlan(null);
    }
  };

  const plans = [
    {
      name: "Plano Mensal",
      price: "R$19.90/mês",
      tier: "mensal",
      days: 30,
      popular: false,
    },
    {
      name: "Plano Anual",
      price: "R$199/ano",
      tier: "anual",
      days: 365,
      popular: true,
      discount: "2 meses de desconto!",
    },
  ];

  return (
    <div className="max-w-4xl mx-auto text-center">
      {showPaymentModal && (
        <PaymentModal
          plan={selectedPlan}
          onClose={() => setShowPaymentModal(false)}
          onConfirm={handleConfirmPayment}
        />
      )}
      <h2 className="text-3xl font-bold mb-4">Nossos Planos</h2>
      <p className="text-gray-600 mb-12">
        Escolha o plano que melhor se adapta às suas necessidades e do seu pet.
      </p>
      <div className="grid md:grid-cols-2 gap-8">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`border rounded-lg p-8 shadow-lg bg-white flex flex-col ${
              plan.popular ? "ring-2 ring-indigo-500" : ""
            }`}
          >
            {plan.popular && (
              <p className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-indigo-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                MAIS POPULAR
              </p>
            )}
            <h3 className="text-2xl font-bold mb-4">{plan.name}</h3>
            <p className="text-4xl font-extrabold mb-4">
              {plan.price.split("/")[0]}
              <span className="text-xl font-medium">
                /{plan.price.split("/")[1]}
              </span>
            </p>
            <ul className="text-left space-y-2 mb-8">
              <li>✅ Geração Ilimitada de Receitas</li>
              <li>✅ Cadastro de Múltiplos Pets</li>
              <li>✅ Calendário Vacinal Completo</li>
              <li>✅ Suporte Prioritário</li>
              {plan.discount && <li>🎉 **{plan.discount}**</li>}
            </ul>
            <button
              onClick={() => handleSelectPlan(plan, plan.days)}
              disabled={processing}
              className="mt-auto w-full py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:bg-indigo-300"
            >
              {processing ? "Processando..." : "Assinar Plano"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const PetManagement = ({ user }) => {
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [selectedPet, setSelectedPet] = useState(null);

  useEffect(() => {
    const petsRef = collection(db, "users", user.uid, "pets");
    const unsubscribe = onSnapshot(
      petsRef,
      (snapshot) => {
        const petsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPets(petsData);
        setLoading(false);
      },
      (error) => {
        console.error("Erro ao buscar pets:", error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [user.uid]);

  const handleViewChange = (newView, pet = null) => {
    setSelectedPet(pet);
    setView(newView);
  };

  if (loading) return <p>Carregando seus pets...</p>;

  if (view === "form") {
    return (
      <PetForm
        user={user}
        pet={selectedPet}
        onFormClose={() => handleViewChange("list")}
      />
    );
  }

  if (view === "vaccines") {
    return (
      <VaccinationCard
        user={user}
        pet={selectedPet}
        onBack={() => handleViewChange("list")}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold">Meus Pets</h2>
        <button
          onClick={() => handleViewChange("form")}
          className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-75"
        >
          + Cadastrar Novo Pet
        </button>
      </div>
      {pets.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pets.map((pet) => (
            <div
              key={pet.id}
              className="bg-white rounded-lg shadow-md p-5 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="text-3xl">
                      {pet.species === "dog" ? "🐶" : "🐱"}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">
                      {pet.name}
                    </h3>
                    <p className="text-sm text-gray-500">{pet.breed}</p>
                  </div>
                </div>
                <div className="text-sm space-y-2">
                  <p>
                    <span className="font-semibold">Nascimento:</span>{" "}
                    {pet.birthDate
                      ? new Date(
                          pet.birthDate + "T00:00:00"
                        ).toLocaleDateString()
                      : "N/A"}
                  </p>
                  <p>
                    <span className="font-semibold">Sexo:</span> {pet.gender}
                  </p>
                  <p>
                    <span className="font-semibold">Peso:</span> {pet.weight} kg
                  </p>
                  <p>
                    <span className="font-semibold">Cor:</span> {pet.color}
                  </p>
                  <p>
                    <span className="font-semibold">Castrado:</span>{" "}
                    {pet.isNeutered}
                  </p>
                  {pet.diseases && (
                    <p>
                      <span className="font-semibold">Doenças:</span>{" "}
                      {pet.diseases}
                    </p>
                  )}
                  {pet.allergies && (
                    <p>
                      <span className="font-semibold">Alergias:</span>{" "}
                      {pet.allergies}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-4 flex flex-col space-y-2">
                <button
                  onClick={() => handleViewChange("vaccines", pet)}
                  className="w-full py-2 px-4 text-sm font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none"
                >
                  <SyringeIcon /> Ver Vacinas
                </button>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => handleViewChange("form", pet)}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() =>
                      deleteDoc(doc(db, "users", user.uid, "pets", pet.id))
                    }
                    className="text-sm font-medium text-red-600 hover:text-red-900"
                  >
                    Excluir
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p>Você ainda não cadastrou nenhum pet.</p>
      )}
    </div>
  );
};

const PetForm = ({ user, pet, onFormClose }) => {
  const [formData, setFormData] = useState({
    name: pet?.name || "",
    species: pet?.species || "dog",
    breed: pet?.breed || "",
    birthDate: pet?.birthDate || "",
    gender: pet?.gender || "Macho",
    weight: pet?.weight || "",
    color: pet?.color || "",
    isNeutered: pet?.isNeutered || "Não",
    diseases: pet?.diseases || "",
    allergies: pet?.allergies || "",
    photoUrl: pet?.photoUrl || "",
    rescheduledDates: pet?.rescheduledDates || {},
  });
  const [isSaving, setIsSaving] = useState(false);
  const [breedInfo, setBreedInfo] = useState("");
  const [isFetchingBreedInfo, setIsFetchingBreedInfo] = useState(false);

  const handleBreedChange = (e) => {
    const { value } = e.target;
    handleChange(e); // Atualiza o formulário normalmente

    const fullBreedList = formData.species === "dog" ? dogBreeds : catBreeds;
    if (fullBreedList.includes(value)) {
      fetchBreedInfo(value);
    } else {
      setBreedInfo("");
    }
  };

  const fetchBreedInfo = useCallback(async (breedName) => {
    setIsFetchingBreedInfo(true);
    setBreedInfo("");
    const prompt = `Você é um especialista em pets. Escreva um texto curto e motivacional (2-3 frases) para um tutor que tem um pet da raça ${breedName}. Destaque uma característica positiva e encantadora da raça. A linguagem deve ser português do Brasil.`;

    try {
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
      const payload = { contents: [{ parts: [{ text: prompt }] }] };
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("API response not ok.");
      const result = await response.json();
      const text = result.candidates?.[0]?.content?.parts?.[0]?.text;
      if (text) {
        setBreedInfo(text);
      }
    } catch (error) {
      console.error("Erro ao buscar informações da raça:", error);
      setBreedInfo(
        "Não foi possível carregar informações sobre esta raça no momento."
      );
    } finally {
      setIsFetchingBreedInfo(false);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "species") {
      setBreedInfo("");
      setFormData((prev) => ({ ...prev, breed: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const petId =
        pet?.id || doc(collection(db, "users", user.uid, "pets")).id;
      const petRef = doc(db, "users", user.uid, "pets", petId);
      await setDoc(petRef, formData, { merge: true });
      onFormClose();
    } catch (error) {
      console.error("Erro ao salvar pet:", error);
      alert("Não foi possível salvar os dados do pet.");
    } finally {
      setIsSaving(false);
    }
  };

  const currentBreedList = formData.species === "dog" ? dogBreeds : catBreeds;

  return (
    <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6">
        {pet ? "Editar Pet" : "Cadastrar Novo Pet"}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700"
            >
              Nome
            </label>
            <input
              type="text"
              name="name"
              id="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label
              htmlFor="species"
              className="block text-sm font-medium text-gray-700"
            >
              Espécie
            </label>
            <select
              name="species"
              id="species"
              value={formData.species}
              onChange={handleChange}
              className="mt-1 block w-full pl-3 pr-10 py-2 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option value="dog">Cachorro</option>
              <option value="cat">Gato</option>
            </select>
          </div>
        </div>
        <div>
          <label
            htmlFor="breed"
            className="block text-sm font-medium text-gray-700"
          >
            Raça
          </label>
          <input
            type="text"
            name="breed"
            id="breed"
            value={formData.breed}
            onChange={handleBreedChange}
            required
            list="breed-list"
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
          <datalist id="breed-list">
            {currentBreedList.map((b) => (
              <option key={b} value={b} />
            ))}
          </datalist>
          {isFetchingBreedInfo && (
            <p className="text-xs text-gray-500 mt-1">
              Buscando informações sobre a raça...
            </p>
          )}
          {breedInfo && (
            <div className="mt-2 text-sm p-3 bg-indigo-50 text-indigo-800 rounded-md">
              {breedInfo}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="birthDate"
              className="block text-sm font-medium text-gray-700"
            >
              Data de Nascimento
            </label>
            <input
              type="date"
              name="birthDate"
              id="birthDate"
              value={formData.birthDate}
              onChange={handleChange}
              required
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label
              htmlFor="gender"
              className="block text-sm font-medium text-gray-700"
            >
              Sexo
            </label>
            <select
              name="gender"
              id="gender"
              value={formData.gender}
              onChange={handleChange}
              className="mt-1 block w-full pl-3 pr-10 py-2 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option>Macho</option>
              <option>Fêmea</option>
            </select>
          </div>
          <div>
            <label
              htmlFor="weight"
              className="block text-sm font-medium text-gray-700"
            >
              Peso (kg)
            </label>
            <input
              type="number"
              step="0.1"
              name="weight"
              id="weight"
              value={formData.weight}
              onChange={handleChange}
              required
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label
              htmlFor="color"
              className="block text-sm font-medium text-gray-700"
            >
              Cor
            </label>
            <input
              type="text"
              name="color"
              id="color"
              value={formData.color}
              onChange={handleChange}
              required
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label
              htmlFor="isNeutered"
              className="block text-sm font-medium text-gray-700"
            >
              Castrado?
            </label>
            <select
              name="isNeutered"
              id="isNeutered"
              value={formData.isNeutered}
              onChange={handleChange}
              className="mt-1 block w-full pl-3 pr-10 py-2 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option>Não</option>
              <option>Sim</option>
            </select>
          </div>
        </div>
        <div className="md:col-span-2">
          <label
            htmlFor="allergies"
            className="block text-sm font-medium text-gray-700"
          >
            Alergias Conhecidas (separe por vírgula)
          </label>
          <textarea
            name="allergies"
            id="allergies"
            value={formData.allergies}
            onChange={handleChange}
            rows="2"
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          ></textarea>
        </div>
        <div className="md:col-span-2">
          <label
            htmlFor="diseases"
            className="block text-sm font-medium text-gray-700"
          >
            Doenças Crônicas (separe por vírgula)
          </label>
          <textarea
            name="diseases"
            id="diseases"
            value={formData.diseases}
            onChange={handleChange}
            rows="2"
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          ></textarea>
        </div>
        <div className="md:col-span-2">
          <label
            htmlFor="photo"
            className="block text-sm font-medium text-gray-700"
          >
            Foto
          </label>
          <input
            type="file"
            name="photo"
            id="photo"
            className="mt-1 w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
          />
          <p className="text-xs text-gray-500 mt-1">
            Upload de fotos em desenvolvimento.
          </p>
        </div>
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onFormClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-lg shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-75 disabled:bg-indigo-300"
          >
            {isSaving ? "Salvando..." : "Salvar Pet"}
          </button>
        </div>
      </form>
    </div>
  );
};

const VaccinationCard = ({ user, pet, onBack }) => {
  const [vaccines, setVaccines] = useState([]);
  const [currentPet, setCurrentPet] = useState(pet);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("history");
  const [editingVaccine, setEditingVaccine] = useState(null);
  const [vaccineToReschedule, setVaccineToReschedule] = useState(null);
  const [error, setError] = useState("");

  const schedule = useMemo(
    () => generateVaccineCalendar(currentPet, vaccines),
    [currentPet, vaccines]
  );

  useEffect(() => {
    const petRef = doc(db, "users", user.uid, "pets", pet.id);
    const unsubPet = onSnapshot(petRef, (docSnap) => {
      if (docSnap.exists()) {
        setCurrentPet({ id: docSnap.id, ...docSnap.data() });
      }
    });

    const vaccinesRef = collection(
      db,
      "users",
      user.uid,
      "pets",
      pet.id,
      "vaccines"
    );
    const q = query(vaccinesRef, orderBy("date", "desc"));
    const unsubVaccines = onSnapshot(
      q,
      (snapshot) => {
        const vaccinesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setVaccines(vaccinesData);
        setLoading(false);
      },
      (err) => {
        console.error("Erro ao buscar vacinas:", err);
        setError("Ocorreu um erro ao carregar as vacinas.");
        setLoading(false);
      }
    );

    return () => {
      unsubPet();
      unsubVaccines();
    };
  }, [user.uid, pet.id]);

  const handleViewChange = (newView, vaccine = null) => {
    setEditingVaccine(vaccine);
    setView(newView);
  };

  if (loading) return <p>Analisando histórico vacinal...</p>;

  if (view === "form") {
    return (
      <VaccineForm
        user={user}
        pet={currentPet}
        vaccine={editingVaccine}
        onFormClose={() => handleViewChange("history")}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {vaccineToReschedule && (
        <RescheduleModal
          user={user}
          pet={currentPet}
          vaccine={vaccineToReschedule}
          onClose={() => setVaccineToReschedule(null)}
        />
      )}
      <button
        onClick={onBack}
        className="mb-4 text-sm font-medium text-indigo-600 hover:text-indigo-900"
      >
        &larr; Voltar para Meus Pets
      </button>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold">Vacinas de {currentPet.name}</h2>
        <button
          onClick={() => handleViewChange("form")}
          className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700"
        >
          + Adicionar Vacina
        </button>
      </div>
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setView("history")}
            className={`${
              view === "history"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Histórico
          </button>
          <button
            onClick={() => setView("schedule")}
            className={`${
              view === "schedule"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Calendário Sugerido
          </button>
        </nav>
      </div>

      {error && <p className="text-red-500">{error}</p>}

      {view === "history" && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {vaccines.length > 0 ? (
              vaccines.map((vaccine) => (
                <li key={vaccine.id} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-lg font-semibold text-gray-800">
                        {vaccine.vaccineName}
                      </p>
                      <p className="text-sm text-gray-600">
                        Aplicada em:{" "}
                        {new Date(vaccine.date).toLocaleDateString()} por Dr(a).{" "}
                        {vaccine.vetName}
                      </p>
                      <p className="text-xs text-gray-500">
                        Lote: {vaccine.lot} | Fabricante: {vaccine.manufacturer}
                      </p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => handleViewChange("form", vaccine)}
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() =>
                          deleteDoc(
                            doc(
                              db,
                              "users",
                              user.uid,
                              "pets",
                              pet.id,
                              "vaccines",
                              vaccine.id
                            )
                          )
                        }
                        className="text-sm font-medium text-red-600 hover:text-red-900"
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                </li>
              ))
            ) : (
              <p className="p-4 text-center text-gray-500">
                Nenhuma vacina registrada para este pet.
              </p>
            )}
          </ul>
        </div>
      )}
      {view === "schedule" && (
        <VaccineSchedule
          schedule={schedule}
          onRescheduleClick={setVaccineToReschedule}
        />
      )}
    </div>
  );
};

const VaccineSchedule = ({ schedule, onRescheduleClick }) => {
  const renderStatus = (item) => {
    const { status, dueDate, originalDueDate } = item;
    const dateString = dueDate ? `em ${dueDate.toLocaleDateString()}` : "";
    const dayOfWeek = dueDate
      ? new Intl.DateTimeFormat("pt-BR", { weekday: "long" }).format(dueDate)
      : "";

    switch (status) {
      case "Aplicada":
        return (
          <div className="flex items-center text-sm font-medium text-green-600">
            <CheckCircle /> <span className="ml-2">Aplicada</span>
          </div>
        );
      case "Atrasada":
        return (
          <button
            onClick={() => onRescheduleClick(item)}
            className="flex items-center text-sm font-medium text-red-500 hover:text-red-700"
          >
            <ExclamationCircle />{" "}
            <span className="ml-2 underline">Atrasada</span>
          </button>
        );
      case "Reagendado":
        return (
          <div className="text-sm text-right">
            <p className="font-semibold flex items-center justify-end text-green-600">
              <CheckCircle />{" "}
              <span className="ml-2">
                Reagendado para {dueDate.toLocaleDateString()} ({dayOfWeek})
              </span>
            </p>
            <p className="text-xs text-gray-500 ml-7">
              Data original: {originalDueDate.toLocaleDateString()}
            </p>
          </div>
        );
      case "Próxima Dose":
        return (
          <div className="flex items-center text-sm font-medium text-yellow-600">
            <ClockIcon />{" "}
            <span className="ml-2">Próxima dose {dateString}</span>
          </div>
        );
      case "Recomendada":
        return (
          <div className="flex items-center text-sm font-medium text-gray-600">
            <PlusCircleIcon /> <span className="ml-2">Recomendada</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h3 className="text-xl font-bold mb-4 text-gray-800">
          Vacinas Obrigatórias
        </h3>
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {schedule.mandatory.map((item) => (
              <li
                key={item.name}
                className="p-4 flex justify-between items-center"
              >
                <p className="font-semibold">{item.name}</p>
                {renderStatus(item)}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div>
        <h3 className="text-xl font-bold mb-4 text-gray-800">
          Vacinas Opcionais
        </h3>
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {schedule.optional.map((item) => (
              <li
                key={item.name}
                className="p-4 flex justify-between items-center"
              >
                <p className="font-semibold">{item.name}</p>
                {renderStatus(item)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const RescheduleModal = ({ user, pet, vaccine, onClose }) => {
  const [newDate, setNewDate] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!newDate) {
      alert("Por favor, selecione uma nova data.");
      return;
    }
    setIsSaving(true);
    const petRef = doc(db, "users", user.uid, "pets", pet.id);
    try {
      await updateDoc(petRef, {
        [`rescheduledDates.${vaccine.name}`]: newDate,
      });
      onClose();
    } catch (error) {
      console.error("Erro ao reagendar vacina:", error);
      alert("Não foi possível salvar o reagendamento.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
        <h3 className="text-lg font-bold mb-4">Reagendar Vacina</h3>
        <p className="mb-2">
          Vacina: <span className="font-semibold">{vaccine.name}</span>
        </p>
        <p className="mb-4 text-sm">
          Data original:{" "}
          <span className="font-semibold">
            {vaccine.dueDate.toLocaleDateString()}
          </span>
        </p>
        <div>
          <label
            htmlFor="newDate"
            className="block text-sm font-medium text-gray-700"
          >
            Nova Data
          </label>
          <input
            type="date"
            id="newDate"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>
        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-indigo-300"
          >
            {isSaving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
};

const VaccineForm = ({ user, pet, vaccine, onFormClose }) => {
  const [formData, setFormData] = useState({
    vaccineName: vaccine?.vaccineName || "",
    lot: vaccine?.lot || "",
    manufacturer: vaccine?.manufacturer || "",
    date: vaccine?.date || "",
    location: vaccine?.location || "",
    vetName: vaccine?.vetName || "",
    proofUrl: vaccine?.proofUrl || "",
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const vaccineId =
        vaccine?.id ||
        doc(collection(db, "users", user.uid, "pets", pet.id, "vaccines")).id;
      const vaccineRef = doc(
        db,
        "users",
        user.uid,
        "pets",
        pet.id,
        "vaccines",
        vaccineId
      );
      await setDoc(vaccineRef, formData, { merge: true });

      const petRef = doc(db, "users", user.uid, "pets", pet.id);
      const petDocSnap = await getDoc(petRef);
      if (petDocSnap.exists()) {
        const petData = petDocSnap.data();
        if (
          petData.rescheduledDates &&
          petData.rescheduledDates[formData.vaccineName]
        ) {
          const newRescheduledDates = { ...petData.rescheduledDates };
          delete newRescheduledDates[formData.vaccineName];
          await updateDoc(petRef, { rescheduledDates: newRescheduledDates });
        }
      }

      onFormClose();
    } catch (error) {
      console.error("Erro ao salvar vacina:", error);
      alert("Não foi possível salvar os dados da vacina.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-md">
      <button
        onClick={onFormClose}
        className="mb-4 text-sm font-medium text-indigo-600 hover:text-indigo-900"
      >
        &larr; Voltar
      </button>
      <h2 className="text-2xl font-bold mb-6">
        {vaccine ? "Editar Registro de Vacina" : "Adicionar Nova Vacina"}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Nome da Vacina
            </label>
            <input
              type="text"
              name="vaccineName"
              value={formData.vaccineName}
              onChange={handleChange}
              required
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Data de Aplicação
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Lote
            </label>
            <input
              type="text"
              name="lot"
              value={formData.lot}
              onChange={handleChange}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Fabricante
            </label>
            <input
              type="text"
              name="manufacturer"
              value={formData.manufacturer}
              onChange={handleChange}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Local de Aplicação
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Veterinário Responsável
            </label>
            <input
              type="text"
              name="vetName"
              value={formData.vetName}
              onChange={handleChange}
              required
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Comprovante
          </label>
          <input
            type="file"
            className="mt-1 w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
          />
          <p className="text-xs text-gray-500 mt-1">
            Upload de comprovantes em desenvolvimento.
          </p>
        </div>
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onFormClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-lg"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg disabled:bg-indigo-300"
          >
            {isSaving ? "Salvando..." : "Salvar Vacina"}
          </button>
        </div>
      </form>
    </div>
  );
};

const NotificationsPage = ({ notifications, onNavigate }) => {
  const getStatusInfo = (alert) => {
    const today = new Date();
    const dueDate = new Date(alert.dueDate);
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (alert.status === "Atrasada") {
      return {
        text: `Atrasada há ${Math.abs(diffDays)} dias`,
        color: "text-red-600",
        icon: <ExclamationCircle />,
      };
    }
    if (alert.status === "Próxima Dose") {
      return {
        text: `Próxima dose em ${diffDays} dias`,
        color: "text-yellow-600",
        icon: <ClockIcon />,
      };
    }
    return { text: "", color: "" };
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold mb-6">Central de Notificações</h2>
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <ul className="divide-y divide-gray-200">
          {notifications.length > 0 ? (
            notifications
              .sort((a, b) => a.dueDate - b.dueDate)
              .map((alert, index) => {
                const { text, color, icon } = getStatusInfo(alert);
                return (
                  <li
                    key={index}
                    className="p-4 hover:bg-gray-50 flex justify-between items-center"
                  >
                    <div>
                      <p className="font-bold text-gray-800">{alert.petName}</p>
                      <p className="text-gray-700">{alert.name}</p>
                      <p
                        className={`text-sm font-semibold ${color} flex items-center mt-1`}
                      >
                        {icon} <span className="ml-1">{text}</span>
                      </p>
                    </div>
                    <button
                      onClick={() => onNavigate("pets")}
                      className="px-3 py-1 bg-blue-500 text-white text-sm font-semibold rounded-md hover:bg-blue-600"
                    >
                      Registrar Vacina
                    </button>
                  </li>
                );
              })
          ) : (
            <p className="p-4 text-center text-gray-500">
              Nenhuma vacina próxima ou atrasada. Tudo em dia!
            </p>
          )}
        </ul>
      </div>
    </div>
  );
};

const RecipeGenerator = ({ user, isSubscriptionActive }) => {
  const [selectedPetId, setSelectedPetId] = useState("");
  const [petType, setPetType] = useState("dog");
  const [petWeight, setPetWeight] = useState("");
  const [userPets, setUserPets] = useState([]);
  const [numberOfDays, setNumberOfDays] = useState("1");
  const [generatedResult, setGeneratedResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [savedMessage, setSavedMessage] = useState("");

  const [isNeutered, setIsNeutered] = useState(false);
  const [diseases, setDiseases] = useState("");
  const [allergies, setAllergies] = useState("");

  useEffect(() => {
    const petsRef = collection(db, "users", user.uid, "pets");
    const unsubscribe = onSnapshot(petsRef, (snapshot) => {
      const petsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUserPets(petsData);
    });
    return () => unsubscribe();
  }, [user.uid]);

  useEffect(() => {
    if (selectedPetId) {
      const selectedPet = userPets.find((p) => p.id === selectedPetId);
      if (selectedPet) {
        setPetType(selectedPet.species);
        setPetWeight(selectedPet.weight);
        setIsNeutered(selectedPet.isNeutered === "Sim");
        setDiseases(selectedPet.diseases || "");
        setAllergies(selectedPet.allergies || "");
      }
    } else {
      setPetWeight("");
      setIsNeutered(false);
      setDiseases("");
      setAllergies("");
    }
  }, [selectedPetId, userPets]);

  const handleGenerateRecipe = async () => {
    const weight = parseFloat(petWeight);
    const days = parseInt(numberOfDays, 10);

    if (!weight || weight <= 0) {
      setError("Por favor, selecione um pet ou insira um peso válido.");
      return;
    }
    if (!days || days <= 0) {
      setError("Por favor, insira um número de dias válido.");
      return;
    }
    if (!isSubscriptionActive) {
      setError(
        "Sua assinatura expirou! Por favor, renove para continuar gerando receitas."
      );
      return;
    }

    setIsLoading(true);
    setError("");
    setGeneratedResult(null);
    setSavedMessage("");

    let dailyPercentage, proteinRatio, organsRatio, vegetablesRatio, carbsRatio;
    let speciesPromptDetails, speciesName;

    if (petType === "dog") {
      speciesName = "cão";
      if (weight <= 10) {
        dailyPercentage = 0.0275;
      } else if (weight <= 25) {
        dailyPercentage = 0.0225;
      } else {
        dailyPercentage = 0.021;
      }
      proteinRatio = 0.55;
      carbsRatio = 0.2;
      organsRatio = 0.075;
      vegetablesRatio = 0.175;
    } else {
      speciesName = "gato";
      dailyPercentage = 0.04;
      proteinRatio = 0.6;
      organsRatio = 0.25;
      vegetablesRatio = 0.05;
      carbsRatio = 0;
    }

    const totalDailyFood = weight * dailyPercentage;
    const totalFoodForPeriod = totalDailyFood * days;
    const totalAmountGrams = (totalFoodForPeriod * 1000).toFixed(0);
    const proteinAmountGrams = (
      totalFoodForPeriod *
      proteinRatio *
      1000
    ).toFixed(0);
    const carbsAmountGrams = (totalFoodForPeriod * carbsRatio * 1000).toFixed(
      0
    );
    const organsAmountGrams = (totalFoodForPeriod * organsRatio * 1000).toFixed(
      0
    );
    const vegetablesAmountGrams = (
      totalFoodForPeriod *
      vegetablesRatio *
      1000
    ).toFixed(0);

    if (petType === "dog") {
      speciesPromptDetails = `
* **Proteínas (aprox. ${proteinAmountGrams}g):** Use carnes magras como peito de frango, patinho bovino, ou peixe.
* **Carboidratos (aprox. ${carbsAmountGrams}g):** Use batata doce, inhame, ou arroz integral bem cozido.
* **Vísceras (aprox. ${organsAmountGrams}g):** Use fígado ou coração de boi/frango.
* **Vegetais (aprox. ${vegetablesAmountGrams}g):** Use uma combinação de 2-3 vegetais como cenoura, abobrinha, ou chuchu.`;
    } else {
      speciesPromptDetails = `
* **Proteínas de Alta Qualidade (aprox. ${proteinAmountGrams}g):** Use carnes como coxa e sobrecoxa de frango (com pele para gordura), moela, ou peixe (sardinha, salmão).
* **Vísceras Essenciais (aprox. ${organsAmountGrams}g):** FOCO EM CORAÇÃO de frango para taurina. Use também fígado de frango em menor quantidade (máximo 5% do total).
* **Vegetais (Opcional, para fibras, aprox. ${vegetablesAmountGrams}g):** Use uma pequena quantidade de abóbora cozida ou cenoura ralada. NÃO USE carboidratos como batata ou arroz.
* **Taurina é CRUCIAL:** A receita DEVE garantir a fonte de taurina através do coração.
* **Gordura:** A gordura deve vir naturalmente da carne (pele do frango). Adicione óleo de peixe (fonte de ômega 3).`;
    }

    const healthConsiderations = [];
    if (isNeutered) {
      healthConsiderations.push(
        "O pet é castrado, portanto a receita deve ser balanceada para controle de peso, com teor calórico moderado."
      );
    }
    if (diseases) {
      healthConsiderations.push(
        `O pet tem as seguintes condições de saúde: ${diseases}. A receita deve ser apropriada para estas condições.`
      );
    }
    if (allergies) {
      healthConsiderations.push(
        `ALERTA DE ALERGIA: O pet é alérgico a: ${allergies}. Estes ingredientes DEVEM SER TOTALMENTE EVITADOS na receita.`
      );
    }

    const prompt = `Você é um nutricionista veterinário especialista em alimentação natural para ${speciesName}s.
Crie uma receita única, detalhada e balanceada para um ${speciesName} adulto saudável de ${weight} kg, para durar ${days} dia(s).
A quantidade total de comida para todo o período é de aproximadamente ${totalAmountGrams}g.
${
  healthConsiderations.length > 0
    ? `\n\n### CONSIDERAÇÕES DE SAÚDE CRÍTICAS ###\n${healthConsiderations.join(
        "\n"
      )}\n`
    : ""
}
Instruções Estritas:
1.  **Distribuição de Macronutrientes (em gramas para o período total):**
    ${speciesPromptDetails}
2.  **Fonte de Gordura:** Adicione uma fonte de gordura saudável na proporção correta.
3.  **Formato JSON:** Retorne a resposta APENAS no formato JSON.
4.  **Campos do JSON:**
    * "name": Crie um nome atrativo para a receita.
    * "description": Escreva uma breve descrição, mencionando a espécie (${speciesName}), que a receita é para ${days} dia(s) e o total em gramas (${totalAmountGrams}g).
    * "ingredients": Liste CADA ingrediente com sua respectiva gramatura TOTAL para o período. A soma das gramas deve ser próxima de ${totalAmountGrams}g.
    * "steps": Forneça um passo a passo claro para o preparo da quantidade total. Inclua uma instrução final sobre como dividir e armazenar as porções diárias.
A linguagem deve ser português do Brasil.`;

    const schema = {
      type: "OBJECT",
      properties: {
        name: { type: "STRING" },
        description: { type: "STRING" },
        ingredients: { type: "ARRAY", items: { type: "STRING" } },
        steps: { type: "ARRAY", items: { type: "STRING" } },
      },
      required: ["name", "description", "ingredients", "steps"],
    };

    try {
      const apiKey = "";
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
      const payload = {
        contents: [{ role: "user", parts: [{ text: prompt }] }],
        generationConfig: {
          responseMimeType: "application/json",
          responseSchema: schema,
        },
      };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`API Error: ${response.statusText}`);

      const result = await response.json();

      if (result.candidates && result.candidates.length > 0) {
        const recipeText = result.candidates[0].content.parts[0].text;
        const recipeJson = JSON.parse(recipeText);
        const recipeWithId = {
          ...recipeJson,
          generatedAt: new Date().toISOString(),
          createdBy: user.uid,
        };

        const docRef = await addDoc(collection(db, "recipes"), recipeWithId);

        let storageSuggestionText = "";
        if (days <= 4) {
          storageSuggestionText =
            "Esta quantidade é ideal para ser mantida sob refrigeração e consumida em até 4 dias.";
        } else {
          storageSuggestionText =
            "Para garantir o frescor, armazene as porções para 3-4 dias na geladeira e congele o restante. Descongele uma porção por dia, conforme for usar.";
        }

        setGeneratedResult({
          recipe: { id: docRef.id, ...recipeWithId },
          storageSuggestion: storageSuggestionText,
        });

        setSavedMessage("Receita personalizada gerada e salva com sucesso!");
      } else {
        throw new Error("A IA não retornou uma receita válida.");
      }
    } catch (err) {
      console.error("Erro ao gerar receita:", err);
      setError(
        "Ocorreu um erro ao gerar a receita. Tente novamente mais tarde."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h2 className="text-2xl font-bold mb-4">
          Gerador de Receitas Personalizadas
        </h2>
        <p className="text-gray-600 mb-6">
          Selecione um de seus pets ou preencha os dados manualmente para criar
          uma receita com as porções exatas.
        </p>

        {!isSubscriptionActive && (
          <div
            className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4"
            role="alert"
          >
            <p className="font-bold">Aviso</p>
            <p>
              Sua assinatura está inativa. A geração de novas receitas está
              desabilitada.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="selectPet"
              className="block text-sm font-medium text-gray-700"
            >
              Selecione um Pet
            </label>
            <select
              id="selectPet"
              value={selectedPetId}
              onChange={(e) => setSelectedPetId(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option value="">-- Preencher manualmente --</option>
              {userPets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="numberOfDays"
              className="block text-sm font-medium text-gray-700"
            >
              Receita para quantos dias?
            </label>
            <input
              type="number"
              id="numberOfDays"
              value={numberOfDays}
              onChange={(e) => setNumberOfDays(e.target.value)}
              placeholder="ex: 7"
              min="1"
              className="mt-1 w-full px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label
              htmlFor="petType"
              className="block text-sm font-medium text-gray-700"
            >
              Tipo de Pet
            </label>
            <select
              id="petType"
              value={petType}
              onChange={(e) => setPetType(e.target.value)}
              disabled={!!selectedPetId}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md disabled:bg-gray-100"
            >
              <option value="dog">Cachorro</option>
              <option value="cat">Gato</option>
            </select>
          </div>
          <div>
            <label
              htmlFor="petWeight"
              className="block text-sm font-medium text-gray-700"
            >
              Peso do Pet (kg)
            </label>
            <input
              type="number"
              id="petWeight"
              value={petWeight}
              onChange={(e) => setPetWeight(e.target.value)}
              disabled={!!selectedPetId}
              placeholder="ex: 5"
              className="mt-1 w-full px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
            />
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={handleGenerateRecipe}
            disabled={isLoading || !isSubscriptionActive}
            className="w-full sm:w-auto flex items-center justify-center px-6 py-3 font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-300 disabled:bg-indigo-300 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <SparklesIcon /> <span className="ml-2">Gerar Receita</span>
              </>
            )}
          </button>
        </div>

        {error && (
          <p className="text-sm text-red-600 mt-4 text-center">{error}</p>
        )}
        {savedMessage && (
          <p className="text-sm text-green-600 mt-4 text-center">
            {savedMessage}
          </p>
        )}
      </div>

      {generatedResult && (
        <div className="mt-8">
          <div
            className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 text-blue-800 rounded-r-lg"
            role="alert"
          >
            <p className="font-bold text-sm">Dica de Armazenamento</p>
            <p className="text-sm">{generatedResult.storageSuggestion}</p>
          </div>
          <RecipeCard
            recipe={generatedResult.recipe}
            user={user}
            showInteractions={true}
          />
        </div>
      )}
    </div>
  );
};

const UserDashboard = ({ user }) => {
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [favoritedRecipes, setFavoritedRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("saved");

  const fetchRecipes = useCallback(
    async (collectionName, setState) => {
      const interactionsRef = collection(db, "users", user.uid, collectionName);
      const unsubscribe = onSnapshot(
        interactionsRef,
        async (snapshot) => {
          const recipeIds = snapshot.docs.map((d) => d.id);
          if (recipeIds.length > 0) {
            try {
              const recipesRef = collection(db, "recipes");
              const q = query(recipesRef, where("__name__", "in", recipeIds));
              const recipeSnapshot = await getDocs(q);
              const recipesData = recipeSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }));
              setState(recipesData);
            } catch (error) {
              console.error("Erro ao buscar receitas linkadas:", error);
            }
          } else {
            setState([]);
          }
        },
        (err) => console.error(`Erro ao observar ${collectionName}:`, err)
      );
      return unsubscribe;
    },
    [user.uid]
  );

  useEffect(() => {
    setLoading(true);
    const unsubSavedPromise = fetchRecipes("saved_recipes", setSavedRecipes);
    const unsubFavoritedPromise = fetchRecipes(
      "favorited_recipes",
      setFavoritedRecipes
    );
    setLoading(false);

    return () => {
      unsubSavedPromise.then((unsub) => unsub());
      unsubFavoritedPromise.then((unsub) => unsub());
    };
  }, [fetchRecipes]);

  return (
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl font-bold mb-6">Minhas Receitas</h2>
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setView("saved")}
            className={`${
              view === "saved"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Receitas Salvas
          </button>
          <button
            onClick={() => setView("favorited")}
            className={`${
              view === "favorited"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Receitas Favoritas
          </button>
        </nav>
      </div>
      {loading ? (
        <p>Carregando...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {view === "saved" &&
            (savedRecipes.length > 0 ? (
              savedRecipes.map((recipe) => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  user={user}
                  showInteractions={true}
                />
              ))
            ) : (
              <p>Você ainda não salvou nenhuma receita.</p>
            ))}
          {view === "favorited" &&
            (favoritedRecipes.length > 0 ? (
              favoritedRecipes.map((recipe) => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  user={user}
                  showInteractions={true}
                />
              ))
            ) : (
              <p>Você ainda não favoritou nenhuma receita.</p>
            ))}
        </div>
      )}
    </div>
  );
};

const RecipeCard = ({ recipe, user, showInteractions = false }) => {
  const [checkedIngredients, setCheckedIngredients] = useState([]);
  const [checkedSteps, setCheckedSteps] = useState([]);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [vote, setVote] = useState(null);

  const favRef = useMemo(
    () => doc(db, "users", user.uid, "favorited_recipes", recipe.id),
    [user.uid, recipe.id]
  );
  const savedRef = useMemo(
    () => doc(db, "users", user.uid, "saved_recipes", recipe.id),
    [user.uid, recipe.id]
  );
  const voteRef = useMemo(
    () => doc(db, "users", user.uid, "recipe_votes", recipe.id),
    [user.uid, recipe.id]
  );

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const [favSnap, savedSnap, voteSnap] = await Promise.all([
          getDoc(favRef),
          getDoc(savedRef),
          getDoc(voteRef),
        ]);
        setIsFavorited(favSnap.exists());
        setIsSaved(savedSnap.exists());
        if (voteSnap.exists()) setVote(voteSnap.data().type);
      } catch (error) {
        console.error("Erro ao verificar status da receita:", error);
      }
    };
    checkStatus();
  }, [favRef, savedRef, voteRef]);

  const handleToggle = async (ref, state, setState) => {
    try {
      if (state) {
        await deleteDoc(ref);
        setState(false);
      } else {
        await setDoc(ref, { addedAt: new Date().toISOString() });
        setState(true);
      }
    } catch (error) {
      console.error("Erro ao salvar/favoritar:", error);
    }
  };

  const handleVote = async (voteType) => {
    try {
      if (vote === voteType) {
        await deleteDoc(voteRef);
        setVote(null);
      } else {
        await setDoc(voteRef, { type: voteType });
        setVote(voteType);
      }
    } catch (error) {
      console.error("Erro ao votar:", error);
    }
  };

  const handleCheck = (list, setList, item) => {
    setList(
      list.includes(item) ? list.filter((i) => i !== item) : [...list, item]
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:-translate-y-1 transition-transform duration-300">
      <div className="p-6">
        <h3 className="text-xl font-bold mb-2 text-gray-800">{recipe.name}</h3>
        <p className="text-sm text-gray-600 mb-4">{recipe.description}</p>
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Ingredientes</h4>
            <ul className="space-y-2">
              {recipe.ingredients.map((ing, i) => (
                <li key={i} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`ing-${recipe.id}-${i}`}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    onChange={() =>
                      handleCheck(checkedIngredients, setCheckedIngredients, i)
                    }
                    checked={checkedIngredients.includes(i)}
                  />
                  <label
                    htmlFor={`ing-${recipe.id}-${i}`}
                    className={`ml-3 text-sm ${
                      checkedIngredients.includes(i)
                        ? "line-through text-gray-400"
                        : "text-gray-700"
                    }`}
                  >
                    {ing}
                  </label>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Modo de Preparo</h4>
            <ol className="space-y-2 list-decimal list-inside">
              {recipe.steps.map((step, i) => (
                <li key={i} className="flex items-start">
                  <input
                    type="checkbox"
                    id={`step-${recipe.id}-${i}`}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer mt-1"
                    onChange={() =>
                      handleCheck(checkedSteps, setCheckedSteps, i)
                    }
                    checked={checkedSteps.includes(i)}
                  />
                  <label
                    htmlFor={`step-${recipe.id}-${i}`}
                    className={`ml-3 text-sm ${
                      checkedSteps.includes(i)
                        ? "line-through text-gray-400"
                        : "text-gray-700"
                    }`}
                  >
                    {step}
                  </label>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
      {showInteractions && (
        <div className="bg-gray-50 px-6 py-3 flex justify-between items-center border-t">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => handleToggle(favRef, isFavorited, setIsFavorited)}
              title="Favoritar"
            >
              <HeartIcon filled={isFavorited} />
            </button>
            <button
              onClick={() => handleToggle(savedRef, isSaved, setIsSaved)}
              title="Salvar"
            >
              <BookmarkIcon filled={isSaved} />
            </button>
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={() => handleVote("like")} title="Gostei">
              <HappyFaceIcon filled={vote === "like"} />
            </button>
            <button onClick={() => handleVote("dislike")} title="Não Gostei">
              <SadFaceIcon filled={vote === "dislike"} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const [currentPage, setCurrentPage] = useState("generator");
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!user) return;

    const petsRef = collection(db, "users", user.uid, "pets");
    const unsubscribePets = onSnapshot(petsRef, (petsSnapshot) => {
      const petPromises = petsSnapshot.docs.map((petDoc) => {
        const petData = { id: petDoc.id, ...petDoc.data() };
        const vaccinesRef = collection(
          db,
          "users",
          user.uid,
          "pets",
          petDoc.id,
          "vaccines"
        );
        return getDocs(vaccinesRef).then((vaccinesSnapshot) => {
          const vaccinesHistory = vaccinesSnapshot.docs.map((vaccineDoc) =>
            vaccineDoc.data()
          );
          return { ...petData, vaccinesHistory };
        });
      });

      Promise.all(petPromises).then((petsWithVaccines) => {
        let allAlerts = [];
        petsWithVaccines.forEach((pet) => {
          const calendar = generateVaccineCalendar(pet, pet.vaccinesHistory);
          const alerts = [...calendar.mandatory, ...calendar.optional]
            .filter(
              (item) =>
                item.status === "Atrasada" || item.status === "Próxima Dose"
            )
            .map((alert) => ({ ...alert, petName: pet.name, petId: pet.id }));
          allAlerts = [...allAlerts, ...alerts];
        });
        setNotifications(allAlerts);
      });
    });

    return () => unsubscribePets();
  }, [user]);

  const handleSignOut = () => {
    signOut(auth).catch((error) => console.error("Erro ao sair:", error));
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true);
      setAuthError("");
      if (currentUser) {
        await currentUser.reload();
        if (currentUser.emailVerified) {
          setIsEmailVerified(true);
          setUser(currentUser);
          const userDocRef = doc(db, "users", currentUser.uid);
          try {
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
              setUserData(userDocSnap.data());
            } else {
              handleSignOut();
            }
          } catch (error) {
            console.error("Firestore Error:", error);
            setAuthError("Ocorreu um erro ao carregar os dados do usuário.");
          }
        } else {
          setIsEmailVerified(false);
          setUser(currentUser);
          setUserData(null);
        }
      } else {
        setUser(null);
        setUserData(null);
        setIsEmailVerified(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const isSubscriptionActive = useMemo(() => {
    if (!userData || !userData.subscriptionEndDate) return false;
    return new Date(userData.subscriptionEndDate) > new Date();
  }, [userData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-xl font-semibold text-gray-700">
          Conectando ao seu Firebase...
        </div>
      </div>
    );
  }

  if (user && !isEmailVerified) {
    return <EmailVerificationMessage user={user} />;
  }

  if (!user) {
    return <AuthComponent setAuthError={setAuthError} authError={authError} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800">
      {authError && (
        <div className="bg-red-500 text-white text-center p-2 fixed top-0 left-0 w-full z-50">
          {authError}
        </div>
      )}
      <header className="bg-white shadow-sm p-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <PawIcon />
          <h1 className="text-2xl font-bold text-gray-800">Pet Chef AI</h1>
        </div>
        <nav className="flex items-center space-x-4">
          {userData?.isAdmin && (
            <button
              onClick={() => setCurrentPage("admin")}
              className={`px-3 py-2 rounded-md text-sm font-medium ${
                currentPage === "admin"
                  ? "bg-indigo-100 text-indigo-700"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              Admin
            </button>
          )}
          <button
            onClick={() => setCurrentPage("generator")}
            className={`px-3 py-2 rounded-md text-sm font-medium ${
              currentPage === "generator"
                ? "bg-indigo-100 text-indigo-700"
                : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            Gerador
          </button>
          <button
            onClick={() => setCurrentPage("pets")}
            className={`px-3 py-2 rounded-md text-sm font-medium ${
              currentPage === "pets"
                ? "bg-indigo-100 text-indigo-700"
                : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            Meus Pets
          </button>
          <button
            onClick={() => setCurrentPage("dashboard")}
            className={`px-3 py-2 rounded-md text-sm font-medium ${
              currentPage === "dashboard"
                ? "bg-indigo-100 text-indigo-700"
                : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            Minhas Receitas
          </button>
          <button
            onClick={() => setCurrentPage("plans")}
            className={`px-3 py-2 rounded-md text-sm font-medium ${
              currentPage === "plans"
                ? "bg-indigo-100 text-indigo-700"
                : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            Planos
          </button>
          <button
            onClick={() => setCurrentPage("account")}
            className={`px-3 py-2 rounded-md text-sm font-medium ${
              currentPage === "account"
                ? "bg-indigo-100 text-indigo-700"
                : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            Minha Conta
          </button>
          <button
            onClick={() => setCurrentPage("notifications")}
            className={`p-2 rounded-md text-sm font-medium ${
              currentPage === "notifications"
                ? "bg-indigo-100 text-indigo-700"
                : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            <BellIcon hasNotification={notifications.length > 0} />
          </button>
          <button
            onClick={handleSignOut}
            className="px-3 py-2 rounded-md text-sm font-medium text-gray-500 hover:bg-gray-100"
          >
            Sair
          </button>
        </nav>
      </header>
      <main className="p-4 md:p-8">
        {currentPage === "admin" && <AdminPanel />}
        {currentPage === "generator" && (
          <RecipeGenerator
            user={user}
            isSubscriptionActive={isSubscriptionActive}
          />
        )}
        {currentPage === "pets" && <PetManagement user={user} />}
        {currentPage === "dashboard" && <UserDashboard user={user} />}
        {currentPage === "plans" && <SubscriptionPage user={user} />}
        {currentPage === "account" && (
          <AccountPage user={user} userData={userData} />
        )}
        {currentPage === "notifications" && (
          <NotificationsPage
            notifications={notifications}
            onNavigate={(page) => setCurrentPage(page)}
          />
        )}
      </main>
      <footer className="text-center p-4 text-xs text-gray-500 border-t mt-8">
        Pet Chef AI © 2024. Gerado com IA para amantes de pets.
      </footer>
    </div>
  );
}
